import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { Button } from '@components/Common';
import { HeadingRow } from '@components/Common/SideBarLayout';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import {
  ActionDirection,
  ShipmentType,
  entryStatus,
  useDownloadWaybillMutation,
} from '@api/queries/inventory';
import { EntryType } from '~types/types';
import { EditorMode } from '../hooks/useBookkeepingEntry';
import { BookkeepingFormData } from '../BookkeepingEntryForm';

const StyledButton = styled(Button)`
  margin-right: 10px;
`;

const WaybillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface Props {
  mode: EditorMode;
  isSaving: boolean;
  isDeleting: boolean;
  hasNextUnconfirmed: boolean;
  entryState: typeof entryStatus[keyof typeof entryStatus];
  isOtherEntry: boolean;
  onSave: () => void;
  onSaveAndNext: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const FormActions: React.FC<Props> = ({
  mode,
  isSaving,
  isDeleting,
  hasNextUnconfirmed,
  entryState,
  isOtherEntry,
  onSave,
  onSaveAndNext,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext<BookkeepingFormData>();

  const downloadWaybillMutation = useDownloadWaybillMutation();

  const entryId = watch('id');
  const shipmentType = watch('shipmentType');
  const actionDirection = watch('actionDirection');
  const entryType = watch('entryType');

  const isNew = mode === 'new';
  const isEdit = mode === 'edit';
  const isApproved = mode === 'approved';
  const isReadOnly = mode === 'readonly';
  const isDoing = isSaving || isDeleting;

  // Determine if waybill can be generated
  const canGenerateWaybill =
    (isEdit || isApproved) &&
    shipmentType === ShipmentType.Truck &&
    actionDirection === ActionDirection.Outgoing &&
    entryType !== EntryType.Other;

  // Handle delete with confirmation
  const handleDelete = () => {
    if (confirm(t('entry.confirm_delete'))) {
      onDelete();
    }
  };

  // Handle waybill download
  const handleWaybillDownload = () => {
    if (entryId) {
      downloadWaybillMutation.mutate(entryId);
    }
  };

  return (
    <HeadingRow>
      {/* Loading indicators */}
      {isSaving && <OperationLoader title={t('saving') + '...'} />}
      {isDeleting && <OperationLoader title={t('deleting') + '...'} color="red" />}

      {/* New entry buttons */}
      {isNew && (
        <div>
          <StyledButton type="submit" tertiary={true} disabled={isDoing}>
            {t('save')}
          </StyledButton>
        </div>
      )}

      {/* Edit entry buttons */}
      {isEdit && (
        <>
          <div>
            <StyledButton type="submit" tertiary={true} disabled={isDoing}>
              {t('update')}
            </StyledButton>
          </div>
          {!isOtherEntry && (
            <div>
              <StyledButton
                type="button"
                tertiary={true}
                disabled={isDoing || !hasNextUnconfirmed}
                onClick={onSaveAndNext}
              >
                {t('entry.save_and_next_unconfirmed')}
              </StyledButton>
            </div>
          )}
          {entryState === entryStatus.incomplete && (
            <Button type="button" danger={true} disabled={isDoing} onClick={handleDelete}>
              {t('delete')}
            </Button>
          )}
        </>
      )}

      {/* Approved entry buttons */}
      {isApproved && (
        <div>
          <StyledButton type="submit" tertiary={true} disabled={isDoing}>
            {t('update')}
          </StyledButton>
        </div>
      )}

      {/* Close button - always shown except in readonly */}
      {!isReadOnly && (
        <StyledButton type="button" secondary={true} disabled={isSaving || isDeleting} onClick={onClose}>
          {t('entry.close_without_save')}
        </StyledButton>
      )}

      {/* Read-only close button */}
      {isReadOnly && (
        <StyledButton type="button" secondary={true} onClick={onClose}>
          {t('close')}
        </StyledButton>
      )}

      {/* Waybill button */}
      {canGenerateWaybill && (
        <WaybillButton
          type="button"
          onClick={handleWaybillDownload}
          disabled={downloadWaybillMutation.isPending}
        >
          <span style={{ display: 'inline-block', width: 14 }}>
            <FileIcon extension="xlsx" {...defaultStyles.xlsx} />
          </span>
          {t('entry.create_waybill')}
        </WaybillButton>
      )}
    </HeadingRow>
  );
};

export default FormActions;
